import { ChatImageComposer, MessageImages } from "@/components/MessageImages";
import { useState, useEffect, useRef, useCallback, type ReactNode } from "react";
import { useAuth } from "@/hooks/use-auth";
import { usePendingOrders } from "@/context/PendingOrdersContext";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Redirect, useSearch } from "wouter";
import {
  ShoppingBag, CheckCircle, XCircle, Truck, Package,
  Clock, Phone, MapPin, Bell, BellOff, Pause, Play,
  ChevronLeft, ChevronRight, RefreshCw, Printer, User, Shield, Star, Map, X, Repeat,
  Loader2, AlertTriangle, ChevronDown, ChevronUp,
} from "lucide-react";
import {RunOrderCard,type PadRun} from "@/components/RunOrderCard";
import {NewRunRequest} from "@/components/NewRunRequest";
import AvailableDriversMap from "@/components/AvailableDriversMap";

type OrderItem = { id: number; name: string; quantity: number; pricePence: number; notes?: string | null; selectedOptions?: string | null; refundedQuantity?: number | null };
type ParsedOption = { groupName?: string; optionName?: string; name?: string; pricePence?: number };
type OptionGroup = { groupName: string; labels: string[] };
type DriverInfo = { driverId: number; driverName: string | null; driverAvatarUrl: string | null; vehicleType: string | null } | null;
type AvailableDriver = {completedCount?:number;isHighValueTrusted?:boolean; id: number; name: string | null; avatarUrl: string | null; vehicleType: string | null; rating: { avg: number; count: number } | null };
type Order = {
  isDemo?: boolean;
  id: number; storeId: number; status: string; fulfilmentType: string;
  customerId?: number | null;
  customerName: string | null; customerPhone: string | null;
  deliveryAddress: string | null; scheduledFor: string | null;
  subtotalPence: number; deliveryFeePence: number; totalPence: number;
  serviceChargePence?: number | null;
  bagChargePence?: number | null;
  customerPlatformFeePence?: number | null;
  customerHubDriverFeePence?: number | null;
  offerDiscountPence?: number | null;
  /* The portion of the discount the SHOP actually funds — the rest is Ayrshire-Hub /
     kindness funded and credited back, so the store keep must subtract only this (matches
     the mobile order pad + Z report). Falls back to offerDiscountPence for legacy rows. */
  shopFundedDiscountPence?: number | null;
  kindnessCreditPence?: number | null;
  kindnessContributionPence?: number | null;
  offerTitle?: string | null;
  paymentMethod?: string | null;
  notes: string | null; createdAt: string; updatedAt: string;
  driverMode?: string | null;
  /* Already returned by the store-orders endpoint; the pad just never read them. */
  dispatchStatus?: string | null;
  statusChangedAt?: string | null;
  requestedDriverId?: number | null;
  driverRequestExpiresAt?: string | null;
  estimatedReadyAt?: string | null;
  isEndToEndOrder?: boolean | null;
  leg2DeliveryFeePence?: number | null;
  isMultiOrderChild?: boolean | null;
  multiOrderId?: number | null;
  companionOrders?: Array<{ orderId: number; storeName: string; status: string; estimatedReadyAt: string | null; legNumber: number; isThisStore: boolean }>;
  pickupSequencePosition?: number;
  pickupSequenceTotal?: number;
  driver?: DriverInfo;
  requestedDriver?: { id: number; name: string | null; avatarUrl: string | null } | null;
  items: OrderItem[];
};
type Store = { salesDemoKitchen?: boolean; id: number; name: string; currentDeliveryMins: number | null; currentCollectionMins: number | null; deliveryTimeMins: number; collectionTimeMins: number; acceptingOrders: boolean; approved: boolean; isActive: boolean; };
type RecurringSchedule = { id: number; customerName: string | null; customerPhone: string | null; itemsJson: string; fulfilmentType: string; deliveryAddress: string | null; frequency: string; preferredTime: string; totalRuns: number; runsRemaining: number; discountPercent: number; totalPerRunPence: number; nextRunAt: string; status: string };

function pence(p: number) { return `£${(p / 100).toFixed(2)}`; }

function addDaysYMD(ymd: string, delta: number) {
  const d = new Date(ymd + "T12:00:00");
  d.setDate(d.getDate() + delta);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/* Single source of truth for what the SHOP keeps on an order — identical to the mobile
   order pad + Z report. Hub-managed delivery isn't the store's to keep; only the
   shop-funded slice of a discount reduces the total (hub/kindness-funded is credited back). */
function orderCharges(order: Order) {
  const isHubDelivery = order.driverMode === "hub";
  const storeDelivery = isHubDelivery ? 0 : (order.deliveryFeePence ?? 0);
  const service = order.serviceChargePence ?? 0;
  const bag = order.bagChargePence ?? 0;
  const totalDiscount = order.offerDiscountPence ?? 0;
  const shopDiscount = Math.max(0, Math.min(order.shopFundedDiscountPence ?? totalDiscount, totalDiscount));
  const hubFundedPromo = Math.max(0, totalDiscount - shopDiscount);
  const kindness = order.kindnessCreditPence ?? 0;
  const youKeep = order.subtotalPence + storeDelivery + service + bag - shopDiscount;
  return { storeDelivery, service, bag, shopDiscount, hubFundedPromo, kindness, youKeep };
}

function Row({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className="flex justify-between text-xs">
      <span className={className ?? "text-slate-500"}>{label}</span>
      <span className={`tabular-nums ${className ?? "text-slate-600"}`}>{value}</span>
    </div>
  );
}

function timeSince(iso: string) {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; next: string | null; nextLabel: string | null }> = {
  pending:          { label: "New Order",             color: "bg-amber-100 text-amber-800 dark:text-amber-300 border-amber-200",    next: "confirmed",       nextLabel: "Accept" },
  confirmed:        { label: "Confirmed",              color: "bg-sky-100 text-sky-800 border-sky-200",          next: "preparing",       nextLabel: "Start Preparing" },
  preparing:        { label: "Preparing",              color: "bg-orange-100 text-orange-800 border-orange-200", next: "ready",           nextLabel: "Ready to Go" },
  ready:            { label: "Ready",                  color: "bg-emerald-100 text-emerald-800 border-emerald-200", next: "out_for_delivery", nextLabel: "Out for Delivery" },
  out_for_delivery: { label: "Out for Delivery",       color: "bg-purple-100 text-purple-800 border-purple-200", next: "delivered",       nextLabel: "Mark Delivered" },
  collected:        { label: "En Route to Shop",       color: "bg-violet-100 text-violet-800 border-violet-200", next: null,              nextLabel: null },
  at_shop:          { label: "At Shop",                color: "bg-yellow-100 text-yellow-800 border-yellow-200", next: null,              nextLabel: null },
  ready_for_return: { label: "Return Dispatched",      color: "bg-teal-100 text-teal-800 border-teal-200",       next: null,              nextLabel: null },
  returning:        { label: "Returning to Customer",  color: "bg-cyan-100 text-cyan-800 border-cyan-200",       next: null,              nextLabel: null },
  delivered:        { label: "Delivered",              color: "bg-slate-100 text-slate-600 border-slate-200",   next: null,              nextLabel: null },
  cancelled:        { label: "Cancelled",              color: "bg-red-100 text-red-700 border-red-200",         next: null,              nextLabel: null },
};


function PrintView({ order }: { order: Order }) {
  // Store receipt shows only what the store earns — platform/hub fees are platform revenue, not shown here.
  // Hub-managed delivery is not charged by the store, so exclude it from the store total.
  const { storeDelivery, shopDiscount, hubFundedPromo, youKeep: merchantTotal } = orderCharges(order);
  return (
    <div className="hidden print:block p-4 text-black text-sm font-mono">
      <div className="text-lg font-bold border-b pb-2 mb-2">ORDER #{order.id}</div>
      <div>{order.fulfilmentType.toUpperCase()} — {new Date(order.createdAt).toLocaleTimeString()}</div>
      {order.customerName && <div>Name: {order.customerName}</div>}
      {order.customerPhone && <div>Tel: {order.customerPhone}</div>}
      {order.deliveryAddress && <div>Addr: {order.deliveryAddress}</div>}
      <div className="border-t my-2" />
      {order.items.map((i) => (
        <div key={i.id}>
          <div className="flex justify-between">
            <span>{i.quantity}x {i.name}</span>
            <span>{pence(i.pricePence * i.quantity)}</span>
          </div>
          {i.selectedOptions && (() => {
            try {
              const opts = JSON.parse(i.selectedOptions) as Array<{ optionName?: string; name?: string }>;
              const labels = opts.map(o => o.optionName ?? o.name ?? "").filter(Boolean);
              return labels.length ? <div className="text-xs pl-2 text-gray-600">{labels.join(", ")}</div> : null;
            } catch { return null; }
          })()}
          {i.notes && <div className="text-xs pl-2 text-gray-500">📝 {i.notes}</div>}
        </div>
      ))}
      <div className="border-t mt-2 pt-2 space-y-1">
        {storeDelivery > 0 && (
          <div className="flex justify-between text-xs"><span>Delivery</span><span>{pence(storeDelivery)}</span></div>
        )}
        {(order.serviceChargePence ?? 0) > 0 && (
          <div className="flex justify-between text-xs"><span>Service charge</span><span>{pence(order.serviceChargePence!)}</span></div>
        )}
        {(order.bagChargePence ?? 0) > 0 && (
          <div className="flex justify-between text-xs"><span>Bag charge</span><span>{pence(order.bagChargePence!)}</span></div>
        )}
        {shopDiscount > 0 && (
          <div className="flex justify-between text-xs"><span>{order.offerTitle ?? "Discount"}</span><span>-{pence(shopDiscount)}</span></div>
        )}
        {hubFundedPromo > 0 && (
          <div className="flex justify-between text-xs text-gray-500"><span>Ayrshire Hub funded (not your cost)</span><span>-{pence(hubFundedPromo)}</span></div>
        )}
        {(order.kindnessCreditPence ?? 0) > 0 && (
          <div className="flex justify-between text-xs text-gray-500"><span>Kindness fund covered (not your cost)</span><span>-{pence(order.kindnessCreditPence!)}</span></div>
        )}
        <div className="flex justify-between font-bold border-t pt-1 mt-1">
          <span>TOTAL</span><span>{pence(merchantTotal)}</span>
        </div>
      </div>
    </div>
  );
}

function AcceptModal({ order, deliveryMins, collectionMins, onConfirm, onCancel }: {
  order: Order; deliveryMins: number; collectionMins: number;
  onConfirm: (mins: number) => void; onCancel: () => void;
}) {
  const defaultMins = order.fulfilmentType === "delivery" ? deliveryMins : collectionMins;
  const [mins, setMins] = useState(defaultMins);
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm">
        <h2 className="font-bold text-lg mb-1">Accept Order #{order.id}</h2>
        <p className="text-sm text-slate-500 mb-5">Set estimated {order.fulfilmentType} time:</p>
        <div className="flex items-center justify-center gap-4 mb-6">
          <button onClick={() => setMins(m => Math.max(5, m - 5))} className="w-12 h-12 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-xl font-bold">−</button>
          <div className="text-center">
            <div className="text-4xl font-black text-primary">{mins}</div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">minutes</div>
          </div>
          <button onClick={() => setMins(m => m + 5)} className="w-12 h-12 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-xl font-bold">+</button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Button variant="outline" onClick={onCancel} className="rounded-xl">Cancel</Button>
          <Button onClick={() => onConfirm(mins)} className="rounded-xl bg-emerald-600 hover:bg-emerald-700">Confirm</Button>
        </div>
      </div>
    </div>
  );
}

function OrderCard({ order, storeId, onStatusChange, onReject, onBanFromCash, onBlockDriver, onRequestDriver, onCancelDriverRequest, onAccept, onReadyForReturn, onProblem, onExtendEta, onReassignDriver, onMessageDriver, deliveryMins, collectionMins }: {
  order: Order;
  storeId: number;
  onStatusChange: (orderId: number, status: string) => Promise<void>;
  onReject: (orderId: number) => Promise<void>;
  onBanFromCash?: (customerId: number) => Promise<void>;
  onBlockDriver?: (driverId: number, driverName: string) => void;
  onRequestDriver?: (orderId: number) => void;
  onCancelDriverRequest?: (orderId: number) => Promise<void>;
  onAccept: (orderId: number, readyInMins: number) => Promise<void>;
  onReadyForReturn?: (orderId: number) => Promise<void>;
  onProblem?: (order: Order) => void;
  onExtendEta?: (orderId: number, extraMins: number) => Promise<void>;
  onReassignDriver?: (orderId: number) => Promise<void>;
  onMessageDriver?: (order: Order) => void;
  deliveryMins: number;
  collectionMins: number;
}) {
  const [accepting, setAccepting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [banning, setBanning] = useState(false);
  const [etaPickerOpen, setEtaPickerOpen] = useState(false);
  const [packMode, setPackMode] = useState(false);
  const [packedIds, setPackedIds] = useState<Set<number>>(new Set());
  const allPacked = order.items.every((i) => packedIds.has(i.id));
  const [cancellingRequest, setCancellingRequest] = useState(false);
  const [readyForReturnBusy, setReadyForReturnBusy] = useState(false);
  const [secsLeft, setSecsLeft] = useState<number | null>(null);
  let cfg = STATUS_CONFIG[order.status] ?? STATUS_CONFIG["pending"]!;
  const isCollection = order.fulfilmentType === "collection";
  const isHubDriver = order.driverMode === "hub" && order.fulfilmentType === "delivery";
  if (isCollection && order.status === "ready") {
    cfg = { ...cfg, next: "delivered", nextLabel: "Mark Collected" };
  } else if (isHubDriver && (order.status === "ready" || order.status === "out_for_delivery")) {
    cfg = { ...cfg, next: null, nextLabel: null };
  }
  const isPending = order.status === "pending";
  const isDone = order.status === "delivered" || order.status === "cancelled";
  const isE2E = order.isEndToEndOrder === true;
  const showReadyForReturn = isE2E && order.status === "at_shop" && !!onReadyForReturn;

  useEffect(() => {
    if (!order.driverRequestExpiresAt) { setSecsLeft(null); return; }
    function compute() {
      const diff = Math.floor((new Date(order.driverRequestExpiresAt!).getTime() - Date.now()) / 1000);
      setSecsLeft(diff > 0 ? diff : 0);
    }
    compute();
    const t = setInterval(compute, 1000);
    return () => clearInterval(t);
  }, [order.driverRequestExpiresAt]);

  async function advance() {
    if (!cfg.next) return;
    if (isPending) { setAccepting(true); return; }
    setBusy(true);
    await onStatusChange(order.id, cfg.next);
    setBusy(false);
  }

  async function handleAccept(mins: number) {
    setAccepting(false);
    setBusy(true);
    await onAccept(order.id, mins);
    setBusy(false);
  }

  function handlePrint() { window.print(); }

  return (
    <>
      {accepting && (
        <AcceptModal
          order={order}
          deliveryMins={deliveryMins}
          collectionMins={collectionMins}
          onConfirm={handleAccept}
          onCancel={() => setAccepting(false)}
        />
      )}
      <PrintView order={order} />
      <div className={`rounded-2xl border-2 bg-white shadow-sm transition-all ${isPending ? "border-amber-300 shadow-amber-100 shadow-lg" : "border-slate-100"}`}>
        {/* Multi-store coordination banner */}
        {order.isMultiOrderChild && order.companionOrders && order.companionOrders.length > 1 && (
          <div className="rounded-t-2xl bg-indigo-50 border-b border-indigo-100 px-4 py-2.5">
            <div className="text-xs font-bold text-indigo-800 mb-1">
              🔗 MULTI-STORE ORDER — Stop {order.pickupSequencePosition} of {order.pickupSequenceTotal}
            </div>
            <div className="space-y-0.5">
              {order.companionOrders.filter((c) => !c.isThisStore).map((c) => (
                <div key={c.orderId} className="flex items-center gap-2 text-xs">
                  <span className={`w-2 h-2 rounded-full ${c.status === "ready" ? "bg-emerald-500" : c.status === "preparing" ? "bg-amber-500" : c.status === "confirmed" ? "bg-blue-500" : "bg-slate-300"}`} />
                  <span className="text-indigo-700 flex-1 min-w-0 truncate">{c.storeName}</span>
                  <span className="text-indigo-500 capitalize">{STATUS_CONFIG[c.status]?.label ?? c.status}</span>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-indigo-500 mt-1">One driver collects every stop — please have this order ready on time.</div>
          </div>
        )}
        {/* Header */}
        <div className="flex items-start justify-between p-4 pb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-black text-foreground text-lg">#{order.id}</span>
              <Badge className={`text-xs font-semibold border ${cfg.color} bg-opacity-100`}>{cfg.label}</Badge>
              {isCollection
                ? <span className="flex items-center gap-1 text-xs text-emerald-700 font-semibold"><Package size={12} /> Collection</span>
                : <span className="flex items-center gap-1 text-xs text-primary font-semibold"><Truck size={12} /> Delivery</span>}
              {isE2E && (
                <span className="flex items-center gap-1 text-xs text-violet-700 font-semibold bg-violet-100 border border-violet-200 rounded-full px-2 py-0.5">
                  <Package size={11} /> Collect &amp; Return
                </span>
              )}
              {order.paymentMethod && (
                <span className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 border ${order.paymentMethod === "cash" ? "bg-orange-100 text-orange-700 border-orange-200" : "bg-sky-100 text-sky-700 border-sky-200"}`}>
                  {order.paymentMethod === "cash" ? "Cash" : "Card"}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span className="flex items-center gap-1"><Clock size={12} /> {timeSince(order.createdAt)}</span>
              {order.scheduledFor && <span className="text-amber-600 font-medium">📅 Scheduled {new Date(order.scheduledFor).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>}
            </div>
            {order.estimatedReadyAt && (
              <div className="flex items-center gap-1 text-xs text-emerald-700 font-semibold mt-1">
                <Clock size={11} />
                <span>Driver pickup by {new Date(order.estimatedReadyAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="font-black text-xl text-foreground">{pence(order.totalPence)}</div>
            <button onClick={handlePrint} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 ml-auto mt-1">
              <Printer size={11} /> Print
            </button>
          </div>
        </div>

        {/* Customer info */}
        <div className="px-4 pb-3 flex flex-wrap gap-3 text-sm">
          {order.customerName && <span className="font-semibold text-slate-800">{order.customerName}</span>}
          {order.customerPhone && (
            <a href={`tel:${order.customerPhone}`} className="flex items-center gap-1 text-primary font-semibold hover:underline">
              <Phone size={13} /> {order.customerPhone}
            </a>
          )}
          {order.deliveryAddress && (
            <span className="flex items-center gap-1 text-slate-600">
              <MapPin size={12} /> {order.deliveryAddress}
            </span>
          )}
          {onBanFromCash && order.customerId && (
            <button
              onClick={async () => {
                if (!order.customerId) return;
                setBanning(true);
                await onBanFromCash(order.customerId);
                setBanning(false);
              }}
              disabled={banning}
              className="ml-auto text-xs text-slate-400 hover:text-red-500 transition-colors disabled:opacity-40"
              title="Prevent this customer from paying with cash at your store"
            >
              🚫 Ban from cash
            </button>
          )}
        </div>

        {/* Driver chip (hub delivery orders) */}
        {order.driverMode === "hub" && (
          <div className="px-4 pb-3">
            {order.driver ? (
              <button
                onClick={() => order.driver && onBlockDriver?.(order.driver.driverId, order.driver.driverName ?? "Driver")}
                className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5 text-sm hover:bg-emerald-100 transition-colors group"
                title="Click to block this driver from your store"
              >
                <div className="w-6 h-6 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xs font-bold overflow-hidden shrink-0">
                  {order.driver.driverAvatarUrl
                    ? <img src={order.driver.driverAvatarUrl} alt="" className="w-full h-full object-cover" />
                    : (order.driver.driverName ?? "D").charAt(0).toUpperCase()}
                </div>
                <Truck size={11} className="text-emerald-600" />
                <span className="font-semibold text-emerald-800 text-xs">{order.driver.driverName ?? "Driver"}</span>
                {order.driver.vehicleType && <span className="text-emerald-500 text-xs capitalize">· {order.driver.vehicleType}</span>}
                <Shield size={11} className="text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
              </button>
            ) : order.requestedDriver && secsLeft !== null && secsLeft > 0 ? (
              <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-full px-3 py-1.5 text-sm">
                <div className="w-6 h-6 rounded-full bg-amber-400 flex items-center justify-center text-white text-xs font-bold overflow-hidden shrink-0">
                  {order.requestedDriver.avatarUrl
                    ? <img src={order.requestedDriver.avatarUrl} alt="" className="w-full h-full object-cover" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
                    : (order.requestedDriver.name ?? "D").charAt(0).toUpperCase()}
                </div>
                <span className="font-semibold text-amber-800 dark:text-amber-300 text-xs">Awaiting {order.requestedDriver.name ?? "driver"}…</span>
                <span className="text-amber-600 text-xs font-mono ml-auto">{secsLeft}s</span>
                {onCancelDriverRequest && (
                  <button
                    onClick={async () => { setCancellingRequest(true); await onCancelDriverRequest(order.id); setCancellingRequest(false); }}
                    disabled={cancellingRequest}
                    className="text-amber-500 hover:text-amber-700 text-xs underline ml-1 disabled:opacity-40"
                  >cancel</button>
                )}
              </div>
            ) : ["pending_dispatch", "offered", "waiting_for_drivers"].includes(order.dispatchStatus ?? "") ? (() => {
              /* The shop used to see nothing here at all while dispatch hunted —
                 order #1330 was marked ready while we had been failing to find a
                 driver for nine minutes. Shows only that we are looking and for
                 how long; which drivers refused is not the shop's business. */
              const since = order.statusChangedAt ? new Date(order.statusChangedAt).getTime() : Date.now();
              const mins = Math.max(0, Math.floor((Date.now() - since) / 60000));
              const cls = mins >= 10 ? "bg-red-50 border-red-200 text-red-700"
                        : mins >= 5  ? "bg-amber-50 border-amber-200 text-amber-700"
                        :              "bg-blue-50 border-blue-200 text-blue-700";
              return (
                <div className={`flex items-center gap-2 border rounded-full px-3 py-1.5 text-sm ${cls}`}>
                  <Loader2 size={13} className="animate-spin shrink-0" />
                  <span className="font-semibold text-xs">
                    {mins >= 10 ? "Still finding a driver" : "Finding a driver…"}
                  </span>
                  <span className="text-xs ml-auto opacity-75">
                    {mins < 1 ? "just started" : `${mins} min`}
                  </span>
                </div>
              );
            })() : order.dispatchStatus === "failed" ? (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-3 py-1.5 text-sm text-red-700">
                <AlertTriangle size={13} className="shrink-0" />
                <span className="font-semibold text-xs">No driver available — Ayrshire Hub alerted</span>
              </div>
            ) : null}
          </div>
        )}

        {/* Items */}
        <div className="px-4 pb-3 space-y-1.5 border-t border-slate-50 pt-3">
          {/* Packing checklist toggle — tick every item before marking ready */}
          {!isDone && ["confirmed", "preparing"].includes(order.status) && (
            <button
              onClick={() => { setPackMode((v) => !v); if (packMode) setPackedIds(new Set()); }}
              className={`w-full flex items-center justify-between text-xs font-semibold rounded-lg px-2 py-1.5 mb-1 border ${packMode ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-slate-50 border-slate-200 text-slate-500"}`}
            >
              <span className="flex items-center gap-1.5"><Package size={12} /> Packing checklist</span>
              <span>{packMode ? `${packedIds.size}/${order.items.length} packed` : "Tap to tick items off"}</span>
            </button>
          )}
          {order.items.map((item) => {
            let parsedOptions: ParsedOption[] = [];
            try { if (item.selectedOptions) parsedOptions = JSON.parse(item.selectedOptions) as ParsedOption[]; } catch { /* ignore */ }
            const groups: OptionGroup[] = [];
            for (const o of parsedOptions) {
              const gn = o.groupName ?? "";
              const label = o.optionName ?? o.name ?? "";
              const last = groups[groups.length - 1];
              if (last && last.groupName === gn) last.labels.push(label);
              else groups.push({ groupName: gn, labels: [label] });
            }
            const packed = packedIds.has(item.id);
            return (
              <div key={item.id} className="flex justify-between items-start text-sm">
                {packMode && (
                  <button
                    onClick={() => setPackedIds((prev) => { const n = new Set(prev); if (n.has(item.id)) n.delete(item.id); else n.add(item.id); return n; })}
                    className={`mr-2 mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 ${packed ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300"}`}
                  >
                    {packed && <CheckCircle size={12} />}
                  </button>
                )}
                <span className={`text-slate-800 flex-1 min-w-0 ${packMode && packed ? "line-through text-slate-400" : ""}`}>
                  <span className="font-bold text-foreground">{item.quantity}×</span> {item.name}
                  {groups.length > 0 && (
                    <span className="block ml-4 mt-0.5">
                      {groups.map((g) => (
                        <span key={g.groupName} className="text-xs text-slate-500 block">
                          {g.groupName && <span className="text-slate-400">{g.groupName}: </span>}{g.labels.join(", ")}
                        </span>
                      ))}
                    </span>
                  )}
                  {item.notes && <span className="text-slate-400 text-xs block ml-4">↳ {item.notes}</span>}
                </span>
                <span className="text-slate-600 font-medium shrink-0 ml-2">{pence(item.pricePence * item.quantity)}</span>
              </div>
            );
          })}
        </div>

        {/* Charges breakdown — same figures the shop keeps (matches mobile pad + print) */}
        {(() => {
          const c = orderCharges(order);
          const rows: ReactNode[] = [];
          if (c.storeDelivery > 0) rows.push(<Row key="d" label="Delivery charge" value={pence(c.storeDelivery)} />);
          if (c.service > 0) rows.push(<Row key="s" label="Service charge" value={pence(c.service)} />);
          if (c.bag > 0) rows.push(<Row key="b" label="Bag charge" value={pence(c.bag)} />);
          if (c.shopDiscount > 0) rows.push(<Row key="disc" label={order.offerTitle ?? "Discount"} value={`-${pence(c.shopDiscount)}`} className="text-rose-600" />);
          if (c.hubFundedPromo > 0) rows.push(<Row key="hub" label="Ayrshire Hub funded (not your cost)" value={`-${pence(c.hubFundedPromo)}`} className="text-slate-400" />);
          if (c.kindness > 0) rows.push(<Row key="k" label="Kindness fund covered (not your cost)" value={`-${pence(c.kindness)}`} className="text-slate-400" />);
          return (
            <div className="px-4 pb-3 pt-1 border-t border-slate-50 space-y-1">
              {rows}
              <div className="flex justify-between text-sm font-bold pt-1 border-t border-slate-100">
                <span className="text-slate-700">You keep</span>
                <span className="text-emerald-700 tabular-nums">{pence(c.youKeep)}</span>
              </div>
            </div>
          );
        })()}

        {/* Notes */}
        {order.notes && (
          <div className="mx-4 mb-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
            📝 {order.notes}
          </div>
        )}

        {/* Actions */}
        {!isDone && (
          <div className={`px-4 pb-4 flex flex-col gap-2 ${isPending ? "pt-1" : ""}`}>
            <div className="flex gap-2">
              {isPending && (
                <Button
                  onClick={() => onReject(order.id)}
                  variant="outline"
                  disabled={busy}
                  className="flex-1 rounded-xl border-red-200 text-red-600 hover:bg-red-50"
                >
                  <XCircle size={15} className="mr-1" /> Reject
                </Button>
              )}
              {cfg.next && (() => {
                // Packing checklist gates the "ready" step: can't mark ready until every item is ticked.
                const blockedByPacking = packMode && cfg.next === "ready" && !allPacked;
                return (
                  <Button
                    onClick={advance}
                    disabled={busy || blockedByPacking}
                    title={blockedByPacking ? "Tick every item on the packing checklist first" : undefined}
                    className={`flex-1 rounded-xl font-bold ${isPending ? "bg-emerald-600 hover:bg-emerald-700 text-white text-base py-5" : "bg-primary hover:bg-primary/90"}`}
                  >
                    {busy ? <RefreshCw size={14} className="animate-spin mr-1" /> : <CheckCircle size={15} className="mr-1" />}
                    {blockedByPacking ? `Pack all items (${packedIds.size}/${order.items.length})` : cfg.nextLabel}
                  </Button>
                );
              })()}
            </div>
            {/* Request specific driver (ready hub delivery, no driver, no pending request) */}
            {order.status === "ready" && order.driverMode === "hub" && !order.driver && !order.requestedDriverId && onRequestDriver && (
              <Button
                onClick={() => onRequestDriver(order.id)}
                variant="outline"
                className="w-full rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 text-xs h-8"
              >
                <User size={12} className="mr-1.5" /> Request Specific Driver
              </Button>
            )}
            {/* Extend estimated time (active orders) */}
            {onExtendEta && ["confirmed", "preparing", "ready", "out_for_delivery"].includes(order.status) && (
              etaPickerOpen ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-slate-500 mr-1">Add:</span>
                  {[5, 10, 15, 20].map((m) => (
                    <Button key={m} size="sm" variant="outline" className="flex-1 rounded-lg h-8 text-xs"
                      onClick={async () => { setEtaPickerOpen(false); await onExtendEta(order.id, m); }}>
                      +{m}
                    </Button>
                  ))}
                  <Button size="sm" variant="ghost" className="h-8 px-2 text-xs" onClick={() => setEtaPickerOpen(false)}>✕</Button>
                </div>
              ) : (
                <Button onClick={() => setEtaPickerOpen(true)} variant="outline" className="w-full rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 text-xs h-8">
                  <Clock size={12} className="mr-1.5" /> Extend time / running late
                </Button>
              )
            )}
            {/* Message / reassign the assigned hub driver */}
            {order.driverMode === "hub" && order.driver && (
              <div className="flex gap-2">
                {onMessageDriver && (
                  <Button onClick={() => onMessageDriver(order)} variant="outline" className="flex-1 rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 text-xs h-8">
                    <Phone size={12} className="mr-1.5" /> Message driver
                  </Button>
                )}
                {onReassignDriver && (
                  <Button onClick={() => onReassignDriver(order.id)} variant="outline" className="flex-1 rounded-xl border-amber-200 text-amber-700 hover:bg-amber-50 text-xs h-8">
                    <Repeat size={12} className="mr-1.5" /> Reassign
                  </Button>
                )}
              </div>
            )}
            {/* Report a problem (refund item / suggest replacement / message / cancel) */}
            {onProblem && order.status !== "pending" && (
              <Button
                onClick={() => onProblem(order)}
                variant="outline"
                className="w-full rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 text-xs h-8"
              >
                <AlertTriangle size={12} className="mr-1.5" /> There's a problem…
              </Button>
            )}
            {/* Ready for Return Delivery — E2E at_shop */}
            {showReadyForReturn && (
              <Button
                onClick={async () => {
                  setReadyForReturnBusy(true);
                  await onReadyForReturn!(order.id);
                  setReadyForReturnBusy(false);
                }}
                disabled={readyForReturnBusy}
                className="w-full rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-bold py-5 text-base"
              >
                {readyForReturnBusy ? <RefreshCw size={16} className="animate-spin mr-2" /> : <Package size={16} className="mr-2" />}
                Order Ready for Return Delivery
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function DriverBlockModal({ driverName, onConfirm, onCancel, loading }: {
  driverName: string; onConfirm: () => void; onCancel: () => void; loading: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
            <Shield size={20} className="text-red-600" />
          </div>
          <div>
            <h2 className="font-bold text-lg text-foreground">Block Driver</h2>
            <p className="text-sm text-slate-500">They won't see your orders</p>
          </div>
        </div>
        <p className="text-sm text-slate-700 mb-5">
          Block <span className="font-semibold">{driverName}</span> from receiving deliveries from your store?
          They won't see your orders in the driver pool and can't claim them.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Button variant="outline" onClick={onCancel} disabled={loading} className="rounded-xl">Cancel</Button>
          <Button onClick={onConfirm} disabled={loading} className="rounded-xl bg-red-600 hover:bg-red-700 text-white">
            {loading ? <RefreshCw size={14} className="animate-spin mr-1" /> : <Shield size={14} className="mr-1" />}
            Block Driver
          </Button>
        </div>
      </div>
    </div>
  );
}

function AvailableDriversModal({ drivers, loading, requestingDriver, onSelect, onCancel }: {
  drivers: AvailableDriver[]; loading: boolean; requestingDriver: boolean;
  onSelect: (driverId: number) => void; onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[80vh] flex flex-col">
        <div className="p-5 border-b border-slate-100">
          <h2 className="font-bold text-lg text-foreground">Request a Driver</h2>
          <p className="text-sm text-slate-500 mt-1">Pick a driver — they get a push notification and have 5 minutes to accept.</p>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw size={24} className="animate-spin text-slate-300" />
            </div>
          ) : drivers.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 gap-2">
              <User size={36} className="text-slate-200" />
              <p className="text-sm text-slate-500">No available drivers right now</p>
            </div>
          ) : (
            <div className="space-y-2">
              {drivers.map((driver) => (
                <button
                  key={driver.id}
                  onClick={() => !requestingDriver && onSelect(driver.id)}
                  disabled={requestingDriver}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 border border-slate-100 text-left transition-colors disabled:opacity-50"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm overflow-hidden shrink-0">
                    {driver.avatarUrl
                      ? <img src={driver.avatarUrl} alt="" className="w-full h-full object-cover" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
                      : (driver.name ?? "D").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-foreground text-sm">{driver.name ?? "Driver"}{driver.isHighValueTrusted&&<span className="ml-2 text-xs text-amber-700">Gold Trusted</span>}</div>
                    {driver.completedCount!=null&&<div className="text-xs text-slate-500">{driver.completedCount.toLocaleString("en-GB")} completed deliveries</div>}
                    {driver.vehicleType && <div className="text-xs text-slate-500 capitalize">{driver.vehicleType}</div>}
                  </div>
                  {driver.rating && (
                    <div className="flex items-center gap-1 text-amber-500 shrink-0">
                      <Star size={12} fill="currentColor" />
                      <span className="text-xs font-semibold">{driver.rating.avg.toFixed(1)}</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="p-4 border-t border-slate-100">
          <Button variant="outline" onClick={onCancel} className="w-full rounded-xl" disabled={requestingDriver}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}

type MenuItem = { id: number; name: string; pricePence: number };

/* Shop-side "there's a problem with this order" flow — mirrors the mobile order pad:
   refund an item, suggest a cheaper/equal replacement, message the customer, or cancel
   the whole order. Same endpoints the mobile pad uses. */
function OrderProblemModal({ order, headers, onDone, onCancelWhole, onClose }: {
  order: Order;
  headers: Record<string, string>;
  onDone: () => void;
  onCancelWhole: (orderId: number) => Promise<void>;
  onClose: () => void;
}) {
  const [view, setView] = useState<"menu" | "refund" | "alt" | "chat">("menu");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const isCash = order.paymentMethod === "cash";

  // --- Refund an item ---
  const [refItemId, setRefItemId] = useState<number | null>(null);
  const [refQty, setRefQty] = useState(1);
  const [refundRequestId] = useState(() => crypto.randomUUID());
  const refItem = order.items.find((i) => i.id === refItemId);
  const itemAvail = (i: OrderItem) => (i.quantity ?? 1) - (((i as { refundedQuantity?: number }).refundedQuantity) ?? 0);
  async function submitRefund() {
    if (!refItemId) return;
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`/api/order/orders/${order.id}/refund-item`, {
        method: "POST", headers,
        body: JSON.stringify({ orderItemId: refItemId, refundQuantity: refQty, reason: "out_of_stock", idempotencyKey: `item:${refundRequestId}:${refItemId}:${refQty}` }),
      });
      const d = await r.json();
      if (!r.ok || !d.ok) { setErr(d.error ?? "Could not issue refund."); return; }
      onDone(); onClose();
    } catch { setErr("Network error."); } finally { setBusy(false); }
  }

  // --- Suggest a replacement ---
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [origItemId, setOrigItemId] = useState<number | null>(null);
  const [altId, setAltId] = useState<number | null>(null);
  const origItem = order.items.find((i) => i.id === origItemId);
  const altItem = menu.find((m) => m.id === altId);
  useEffect(() => {
    if (view !== "alt" || menu.length > 0) return;
    void (async () => {
      try {
        const sr = await fetch(`/api/order/stores/${order.storeId}/menu-sections`, { headers });
        const sd = await sr.json();
        const sections: Array<{ id: number }> = sd.items ?? [];
        const all: MenuItem[] = [];
        await Promise.all(sections.map(async (sec) => {
          const r = await fetch(`/api/order/stores/${order.storeId}/menu-items?sectionId=${sec.id}`, { headers });
          const d = await r.json();
          all.push(...((d.items ?? []) as MenuItem[]));
        }));
        setMenu(all);
      } catch { /* silent */ }
    })();
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps
  async function submitAlt() {
    if (!origItemId || !altId || !altItem) return;
    if (altItem.pricePence > (origItem?.pricePence ?? 0)) { setErr("Replacement must be the same price or cheaper — the shop absorbs any difference."); return; }
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`/api/order/orders/${order.id}/offer-alternative`, {
        method: "POST", headers,
        body: JSON.stringify({ originalOrderItemId: origItemId, alternativeMenuItemId: altId, alternativeName: altItem.name, alternativePricePence: altItem.pricePence }),
      });
      const d = await r.json();
      if (!r.ok || !d.ok) { setErr(d.error ?? "Could not send offer."); return; }
      onDone(); onClose();
    } catch { setErr("Network error."); } finally { setBusy(false); }
  }

  // --- Message the customer ---
  type Msg = { id: number; senderRole: string; text: string; body?: string; attachmentIds?: string[]; sentAt: string };
  const [convoId, setConvoId] = useState<number | null>(null);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [draft, setDraft] = useState("");
  useEffect(() => {
    if (view !== "chat") return;
    let stop = false;
    async function ensure() {
      try {
        const cr = await fetch(`/api/order/orders/${order.id}/conversation`, { method: "POST", headers });
        const cd = await cr.json();
        if (!cr.ok) { setErr(cd.error ?? "Could not open chat."); return; }
        if (!stop) setConvoId(cd.conversationId);
      } catch { setErr("Network error."); }
    }
    void ensure();
    const load = async () => {
      try {
        const r = await fetch(`/api/order/orders/${order.id}/conversation`, { headers });
        const d = await r.json();
        if (!stop) setMsgs((d.messages ?? []) as Msg[]);
      } catch { /* ignore */ }
    };
    void load();
    const t = setInterval(load, 4000);
    return () => { stop = true; clearInterval(t); };
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps
  async function sendMsg() {
    const text = draft.trim();
    if (!text || !convoId) return;
    setDraft("");
    try {
      await fetch(`/api/order/conversations/${convoId}/messages`, { method: "POST", headers, body: JSON.stringify({ text }) });
      const r = await fetch(`/api/order/orders/${order.id}/conversation`, { headers });
      const d = await r.json();
      setMsgs((d.messages ?? []) as Msg[]);
    } catch { setErr("Could not send."); }
  }

  const menuBtn = "w-full flex items-start gap-3 p-3.5 rounded-xl border text-left transition-colors";
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-100 flex items-center gap-2">
          {view !== "menu" && <button onClick={() => { setView("menu"); setErr(null); }} className="text-slate-400 hover:text-slate-700"><ChevronLeft size={20} /></button>}
          <h2 className="font-bold text-lg text-foreground flex-1">
            {view === "menu" ? `Problem with order #${order.id}` : view === "refund" ? "Refund an item" : view === "alt" ? "Suggest a replacement" : "Message customer"}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="p-4 overflow-y-auto">
          {err && <p className="text-sm text-red-600 mb-3">{err}</p>}

          {view === "menu" && (
            <div className="space-y-2">
              {!isCash && (
                <button className={`${menuBtn} border-slate-200 hover:bg-slate-50`} onClick={() => { setErr(null); setView("refund"); }}>
                  <XCircle size={20} className="text-primary mt-0.5" /><span><span className="block font-bold text-sm">Refund an item</span><span className="block text-xs text-slate-500">One item isn't available — refund it, keep the rest</span></span>
                </button>
              )}
              <button className={`${menuBtn} border-slate-200 hover:bg-slate-50`} onClick={() => { setErr(null); setView("alt"); }}>
                <Repeat size={20} className="text-primary mt-0.5" /><span><span className="block font-bold text-sm">Suggest a replacement</span><span className="block text-xs text-slate-500">Offer an alternative product to the customer</span></span>
              </button>
              <button className={`${menuBtn} border-slate-200 hover:bg-slate-50`} onClick={() => { setErr(null); setView("chat"); }}>
                <Phone size={20} className="text-primary mt-0.5" /><span><span className="block font-bold text-sm">Message customer</span><span className="block text-xs text-slate-500">Contact them about this order</span></span>
              </button>
              <button className={`${menuBtn} border-red-200 bg-red-50/40 hover:bg-red-50`} onClick={async () => {
                if (!window.confirm(isCash ? "Cancel this whole order? (No card payment to reverse.)" : "Cancel and fully refund this whole order? This is a last resort.")) return;
                setBusy(true); await onCancelWhole(order.id); setBusy(false); onDone(); onClose();
              }}>
                <XCircle size={20} className="text-red-600 mt-0.5" /><span><span className="block font-bold text-sm text-red-600">Cancel whole order</span><span className="block text-xs text-red-500/80">{isCash ? "Cancel order — no card payment to reverse" : "Refund everything — last resort only"}</span></span>
              </button>
            </div>
          )}

          {view === "refund" && (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">Select the item you cannot fulfil:</p>
              {order.items.every((i) => itemAvail(i) <= 0) && <p className="text-sm text-slate-500">All items on this order have already been refunded.</p>}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {order.items.map((i) => {
                  const avail = itemAvail(i);
                  if (avail <= 0) return null;
                  const sel = refItemId === i.id;
                  return (
                    <button key={i.id} onClick={() => { setRefItemId(i.id); setRefQty(1); }} className={`w-full flex items-center justify-between gap-2 p-3 rounded-xl border text-left ${sel ? "border-primary bg-primary/5" : "border-slate-200"}`}>
                      <span><span className="block font-semibold text-sm">{i.name}</span><span className="block text-xs text-slate-500">{pence(i.pricePence)} × {avail} available</span></span>
                      {sel && <CheckCircle size={18} className="text-primary" />}
                    </button>
                  );
                })}
              </div>
              {refItem && itemAvail(refItem) > 1 && (
                <div className="flex items-center gap-3 text-sm"><span>Refund quantity:</span>
                  <button onClick={() => setRefQty((q) => Math.max(1, q - 1))} className="w-8 h-8 rounded-full border">−</button>
                  <span className="font-bold">{refQty}</span>
                  <button onClick={() => setRefQty((q) => Math.min(q + 1, itemAvail(refItem)))} className="w-8 h-8 rounded-full border">+</button>
                </div>
              )}
              <Button onClick={submitRefund} disabled={busy || !refItemId} className="w-full rounded-xl bg-primary">{busy ? <RefreshCw size={14} className="animate-spin mr-1" /> : null}Confirm refund</Button>
            </div>
          )}

          {view === "alt" && (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-slate-600">Item to replace:</p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {order.items.map((i) => {
                  if (itemAvail(i) <= 0) return null;
                  const sel = origItemId === i.id;
                  return <button key={i.id} onClick={() => setOrigItemId(i.id)} className={`w-full flex items-center justify-between gap-2 p-2.5 rounded-xl border text-left ${sel ? "border-primary bg-primary/5" : "border-slate-200"}`}><span className="text-sm font-semibold">{i.name}</span><span className="text-xs text-slate-500">{pence(i.pricePence)}</span></button>;
                })}
              </div>
              <p className="text-sm font-semibold text-slate-600">Replace with (same price or cheaper):</p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {menu.length === 0 && <p className="text-sm text-slate-400">Loading menu…</p>}
                {menu.filter((m) => m.pricePence <= (origItem?.pricePence ?? Infinity)).map((m) => {
                  const sel = altId === m.id;
                  return <button key={m.id} onClick={() => setAltId(m.id)} disabled={!origItemId} className={`w-full flex items-center justify-between gap-2 p-2.5 rounded-xl border text-left disabled:opacity-40 ${sel ? "border-primary bg-primary/5" : "border-slate-200"}`}><span className="text-sm">{m.name}</span><span className="text-xs text-slate-500">{pence(m.pricePence)}</span></button>;
                })}
              </div>
              <Button onClick={submitAlt} disabled={busy || !origItemId || !altId} className="w-full rounded-xl bg-primary">{busy ? <RefreshCw size={14} className="animate-spin mr-1" /> : null}Send offer to customer</Button>
            </div>
          )}

          {view === "chat" && (
            <div className="flex flex-col h-96">
              <div className="flex-1 overflow-y-auto space-y-2 mb-3">
                {msgs.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No messages yet. Say hello 👋</p>}
                {msgs.map((m) => (
                  <div key={m.id} className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.senderRole === "shop" ? "ml-auto bg-primary text-white" : "bg-slate-100 text-slate-800"}`}><MessageImages ids={m.attachmentIds}/>{m.body ?? m.text}</div>
                ))}
              </div>
              <div className="flex gap-2">
                {convoId&&<ChatImageComposer key={convoId} endpoint={`/api/order/conversations/${convoId}/messages`} bodyKey="text"/>}
<input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void sendMsg(); }} placeholder="Type a message…" className="flex-1 border rounded-xl px-3 py-2 text-sm" />
                <Button onClick={() => void sendMsg()} disabled={!draft.trim() || !convoId} className="rounded-xl bg-primary">Send</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* Shop ↔ driver chat for a hub-delivery order — mirrors the mobile pad's driver chat. */
function DriverChatModal({ order, headers, onClose }: {
  order: Order; headers: Record<string, string>; onClose: () => void;
}) {
  type DMsg = { id: number; senderUserId: number; body: string; attachmentIds?: string[]; createdAt: string; senderName: string | null };
  const [msgs, setMsgs] = useState<DMsg[]>([]);
  const [draft, setDraft] = useState("");
  const driverId = order.driver?.driverId;
  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const r = await fetch(`/api/orders/${order.id}/driver-messages`, { headers });
        if (!r.ok) return;
        const d = await r.json();
        if (!stop) setMsgs((d.items ?? []) as DMsg[]);
      } catch { /* ignore */ }
    };
    void load();
    const t = setInterval(load, 5000);
    return () => { stop = true; clearInterval(t); };
  }, [order.id]); // eslint-disable-line react-hooks/exhaustive-deps
  async function send() {
    const body = draft.trim();
    if (!body || !driverId) return;
    setDraft("");
    try {
      await fetch(`/api/orders/${order.id}/driver-messages`, { method: "POST", headers, body: JSON.stringify({ body, recipientUserId: driverId }) });
      const r = await fetch(`/api/orders/${order.id}/driver-messages`, { headers });
      const d = await r.json();
      setMsgs((d.items ?? []) as DMsg[]);
    } catch { /* ignore */ }
  }
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-100 flex items-center gap-2">
          <h2 className="font-bold text-lg text-foreground flex-1">Message {order.driver?.driverName ?? "driver"} — order #{order.id}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="p-4 flex flex-col h-96">
          <div className="flex-1 overflow-y-auto space-y-2 mb-3">
            {msgs.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No messages yet.</p>}
            {msgs.map((m) => (
              <div key={m.id} className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.senderUserId === driverId ? "bg-slate-100 text-slate-800" : "ml-auto bg-primary text-white"}`}><MessageImages ids={m.attachmentIds}/>{m.body}</div>
            ))}
          </div>
          <div className="flex gap-2">
            {driverId&&<ChatImageComposer key={`${order.id}:${driverId}`} endpoint={`/api/orders/${order.id}/driver-messages`} recipientUserId={driverId}/>}
<input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void send(); }} placeholder="Message the driver…" className="flex-1 border rounded-xl px-3 py-2 text-sm" />
            <Button onClick={() => void send()} disabled={!draft.trim()} className="rounded-xl bg-primary">Send</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OrderPad() {
  const { user } = useAuth();
  const { toast } = useToast();
  const search = useSearch();
  const targetStoreId = new URLSearchParams(search).get("storeId") ? Number(new URLSearchParams(search).get("storeId")) : null;
  const [demoBusy, setDemoBusy] = useState(false);
  const demoRequest = useRef<{ mode: string; id: string } | null>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(targetStoreId);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const { alertsOn, setAlertsOn, clearBadge } = usePendingOrders();
  const [tab, setTab] = useState<"incoming" | "active" | "done" | "runs">("incoming");
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [blockDriverModal, setBlockDriverModal] = useState<{ driverId: number; driverName: string } | null>(null);
  const [requestDriverModal, setRequestDriverModal] = useState<{ orderId: number } | null>(null);
  const [availableDrivers, setAvailableDrivers] = useState<AvailableDriver[]>([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [blockingDriver, setBlockingDriver] = useState(false);
  const [requestingDriver, setRequestingDriver] = useState(false);
  const [showDriverMap, setShowDriverMap] = useState(false);
  const [etaEdit, setEtaEdit] = useState<{ type: "delivery" | "collection"; value: string } | null>(null);
  const [showPausePicker, setShowPausePicker] = useState(false);
  const [recurringSchedules, setRecurringSchedules] = useState<RecurringSchedule[]>([]);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [externalRuns,setExternalRuns]=useState<PadRun[]>([]);
  const [runsError,setRunsError]=useState("");
  const [newRun,setNewRun]=useState(false);
  const runRequest=useRef(0);
  const [problemOrder, setProblemOrder] = useState<Order | null>(null);
  const [driverChatOrder, setDriverChatOrder] = useState<Order | null>(null);
  const todayYMD = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
  const [viewDate, setViewDate] = useState<string>(todayYMD());
  const isToday = viewDate === todayYMD();

  const token = typeof window !== "undefined" ? localStorage.getItem("ayrshire_token") : null;
  const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };

  const selectedStore = stores.find((s) => s.id === selectedStoreId);

  async function sendTestOrder(mode: "collection" | "store_delivery") {
    if (demoBusy) return;
    setDemoBusy(true);
    if (!demoRequest.current || demoRequest.current.mode !== mode) demoRequest.current = { mode, id: crypto.randomUUID() };
    try {
      const r = await fetch("/api/sales/demo/orders", { method: "POST", headers,
        body: JSON.stringify({ mode, requestId: demoRequest.current.id }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error ?? "Could not send test order");
      demoRequest.current = null; setViewDate(todayYMD()); setTab("incoming");
      await fetchOrders(); toast({ title: "Test order sent", description: "Accept it on the order pad to begin the demonstration." });
    } catch (e) { toast({ title: "Test order failed", description: (e as Error).message, variant: "destructive" }); }
    finally { setDemoBusy(false); }
  }

  async function loadStores() {
    const r = await fetch("/api/order/my-stores", { headers });
    const d = await r.json() as { items: Store[] };
    let items: Store[] = d.items ?? [];

    // Admin managing a specific store not in their own list — fetch and inject it
    if (targetStoreId && !items.some((s) => s.id === targetStoreId)) {
      const sr = await fetch(`/api/order/stores/${targetStoreId}`, { headers });
      if (sr.ok) {
        const sd = await sr.json() as { store: Store };
        if (sd.store) items = [sd.store, ...items];
      }
    }

    setStores(items);
    if (!selectedStoreId && items[0]) setSelectedStoreId(targetStoreId ?? items[0].id);
  }

  const fetchOrders = useCallback(async () => {
    if (!selectedStoreId) return;
    try {
      const r = await fetch(`/api/order/stores/${selectedStoreId}/orders?date=${viewDate}`, { headers });
      const d = await r.json() as { items: Order[] };
      const incoming = d.items ?? [];
      setOrders(incoming);

      // Only clear the new-order badge for the live (today) view.
      if (viewDate === todayYMD()) {
        const pendingIds = incoming.filter(o => o.status === "pending").map(o => o.id);
        clearBadge(pendingIds);
      }
    } catch { /* silent */ }
    setLoading(false);
  }, [selectedStoreId, clearBadge, viewDate]);

  async function fetchRecurringSchedules() {
    if (!selectedStoreId) return;
    try {
      const r = await fetch(`/api/order/stores/${selectedStoreId}/recurring-schedules`, { headers });
      if (r.ok) {
        const d = await r.json() as { schedules: RecurringSchedule[] };
        setRecurringSchedules(d.schedules ?? []);
      }
    } catch { /* silent */ }
  }

  const fetchExternalRuns=useCallback(async()=>{
    if(!selectedStoreId)return;const request=++runRequest.current;
    try{const r=await fetch(`/api/external-runs/mine?storeId=${selectedStoreId}&date=${viewDate}`,{headers:{Authorization:`Bearer ${token}`},cache:"no-store"});const d=await r.json();if(!r.ok)throw Error(d.error??"Could not refresh delivery runs");if(request===runRequest.current){setExternalRuns(d.runs??[]);setRunsError("");}}
    catch(e){if(request===runRequest.current)setRunsError((e as Error).message);}
  },[selectedStoreId,viewDate,token]);

  useEffect(() => { void loadStores(); }, []);

  useEffect(() => {
    if (!selectedStoreId) return;
    setLoading(true);
    setExternalRuns([]);
    void fetchOrders();
    void fetchRecurringSchedules();
    void fetchExternalRuns();
    pollRef.current = setInterval(() => { void fetchOrders();void fetchExternalRuns(); }, 15000);
    const resume=()=>{if(document.visibilityState!=="hidden"){void fetchOrders();void fetchExternalRuns();}};window.addEventListener("focus",resume);document.addEventListener("visibilitychange",resume);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      ++runRequest.current;window.removeEventListener("focus",resume);document.removeEventListener("visibilitychange",resume);
    };
  }, [selectedStoreId, fetchOrders,fetchExternalRuns]);

  async function updateStatus(orderId: number, status: string) {
    await fetch(`/api/order/orders/${orderId}/status`, { method: "PATCH", headers, body: JSON.stringify({ status }) });
    await fetchOrders();
  }

  async function acceptOrder(orderId: number, readyInMins: number) {
    await fetch(`/api/order/orders/${orderId}/status`, { method: "PATCH", headers, body: JSON.stringify({ status: "confirmed", readyInMins }) });
    await fetchOrders();
  }

  async function rejectOrder(orderId: number) {
    await updateStatus(orderId, "cancelled");
  }

  async function extendEta(orderId: number, extraMins: number) {
    try {
      const r = await fetch(`/api/order/orders/${orderId}/eta`, {
        method: "PATCH", headers, body: JSON.stringify({ extraMins }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); toast({ title: d.error ?? "Failed to extend time", variant: "destructive" }); return; }
      toast({ title: `Added ${extraMins} min to the estimated time` });
      await fetchOrders();
    } catch { toast({ title: "Network error", variant: "destructive" }); }
  }

  async function reassignDriver(orderId: number) {
    if (!window.confirm("Reassign driver? This cancels the current driver assignment and re-broadcasts the order to available drivers.")) return;
    try {
      const r = await fetch(`/api/orders/${orderId}/driver-assignment`, { method: "DELETE", headers });
      if (!r.ok) { const d = await r.json().catch(() => ({})); toast({ title: d.error ?? "Could not reassign driver", variant: "destructive" }); return; }
      toast({ title: "Driver reassigned", description: "Re-broadcasting to available drivers" });
      await fetchOrders();
    } catch { toast({ title: "Network error", variant: "destructive" }); }
  }

  async function banFromCash(customerId: number) {
    if (!selectedStoreId) return;
    if (!window.confirm("Ban this customer from cash orders at your store?")) return;
    try {
      await fetch(`/api/order/stores/${selectedStoreId}/cash-bans`, {
        method: "POST", headers,
        body: JSON.stringify({ userId: customerId }),
      });
      toast({ title: "Customer banned from cash orders at this store." });
    } catch {
      toast({ title: "Failed to ban customer", variant: "destructive" });
    }
  }

  async function pauseFor(durationMins: number | "resume") {
    if (!selectedStoreId || !selectedStore) return;
    setShowPausePicker(false);
    if (durationMins === "resume") {
      const r = await fetch(`/api/order/stores/${selectedStoreId}/operational`, {
        method: "PATCH", headers, body: JSON.stringify({ acceptingOrders: true }),
      });
      if (!r.ok) { toast({ title: "Failed to resume orders", variant: "destructive" }); return; }
      setStores(prev => prev.map(s => s.id === selectedStoreId ? { ...s, acceptingOrders: true } : s));
      toast({ title: "Orders resumed", description: "You are now accepting orders" });
    } else {
      const pausedUntil = durationMins === -1
        ? undefined // let server compute next opening time
        : new Date(Date.now() + durationMins * 60_000).toISOString();
      const r = await fetch(`/api/order/stores/${selectedStoreId}/operational`, {
        method: "PATCH", headers,
        body: JSON.stringify({ acceptingOrders: false, ...(pausedUntil ? { pausedUntil } : {}) }),
      });
      if (!r.ok) { toast({ title: "Failed to pause orders", variant: "destructive" }); return; }
      setStores(prev => prev.map(s => s.id === selectedStoreId ? { ...s, acceptingOrders: false } : s));
      const desc = durationMins === 15 ? "for 15 minutes" : durationMins === 60 ? "for 1 hour" : "until tomorrow";
      toast({ title: "Orders paused", description: `Paused ${desc} — will resume automatically` });
    }
  }

  async function changeDeliveryTime(delta: number) {
    if (!selectedStoreId || !selectedStore) return;
    const current = selectedStore.currentDeliveryMins ?? selectedStore.deliveryTimeMins;
    const newVal = Math.max(5, current + delta);
    const r = await fetch(`/api/order/stores/${selectedStoreId}/operational`, {
      method: "PATCH", headers,
      body: JSON.stringify({ currentDeliveryMins: newVal }),
    });
    if (r.ok) setStores(prev => prev.map(s => s.id === selectedStoreId ? { ...s, currentDeliveryMins: newVal } : s));
  }

  async function changeCollectionTime(delta: number) {
    if (!selectedStoreId || !selectedStore) return;
    const current = selectedStore.currentCollectionMins ?? selectedStore.collectionTimeMins;
    const newVal = Math.max(5, current + delta);
    const r = await fetch(`/api/order/stores/${selectedStoreId}/operational`, {
      method: "PATCH", headers,
      body: JSON.stringify({ currentCollectionMins: newVal }),
    });
    if (r.ok) setStores(prev => prev.map(s => s.id === selectedStoreId ? { ...s, currentCollectionMins: newVal } : s));
  }

  async function applyCustomEta(type: "delivery" | "collection", rawValue: string) {
    if (!selectedStoreId || !selectedStore) return;
    const parsed = Number(rawValue.trim());
    if (isNaN(parsed) || parsed < 1) return;
    const mins = Math.max(1, Math.round(parsed));
    const key = type === "delivery" ? "currentDeliveryMins" : "currentCollectionMins";
    await fetch(`/api/order/stores/${selectedStoreId}/operational`, {
      method: "PATCH", headers,
      body: JSON.stringify({ [key]: mins }),
    });
    setStores(prev => prev.map(s => s.id === selectedStoreId ? { ...s, [key]: mins } : s));
    setEtaEdit(null);
  }

  function fmtMins(mins: number) {
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }

  async function openRequestDriverModal(orderId: number) {
    if (!selectedStoreId) return;
    setRequestDriverModal({ orderId });
    setLoadingDrivers(true);
    try {
      const r = await fetch(`/api/order/stores/${selectedStoreId}/available-drivers`, { headers });
      const d = await r.json() as { items: AvailableDriver[] };
      setAvailableDrivers(d.items ?? []);
    } catch {
      toast({ title: "Failed to load drivers", variant: "destructive" });
      setRequestDriverModal(null);
    } finally {
      setLoadingDrivers(false);
    }
  }

  async function confirmBlockDriver() {
    if (!blockDriverModal || !selectedStoreId) return;
    setBlockingDriver(true);
    try {
      await fetch(`/api/order/stores/${selectedStoreId}/driver-blocks`, {
        method: "POST", headers,
        body: JSON.stringify({ driverId: blockDriverModal.driverId }),
      });
      toast({ title: `${blockDriverModal.driverName} blocked from your store` });
      setBlockDriverModal(null);
      await fetchOrders();
    } catch {
      toast({ title: "Failed to block driver", variant: "destructive" });
    } finally {
      setBlockingDriver(false);
    }
  }

  async function confirmRequestDriver(driverId: number) {
    if (!requestDriverModal || !selectedStoreId) return;
    setRequestingDriver(true);
    try {
      const r = await fetch(`/api/order/stores/${selectedStoreId}/orders/${requestDriverModal.orderId}/request-driver`, {
        method: "POST", headers,
        body: JSON.stringify({ driverId }),
      });
      if (!r.ok) {
        const d = await r.json() as { error?: string };
        toast({ title: d.error ?? "Failed to request driver", variant: "destructive" });
        return;
      }
      const driver = availableDrivers.find((d) => d.id === driverId);
      toast({ title: `Request sent to ${driver?.name ?? "driver"}`, description: "They have 5 minutes to accept." });
      setRequestDriverModal(null);
      await fetchOrders();
    } catch {
      toast({ title: "Failed to request driver", variant: "destructive" });
    } finally {
      setRequestingDriver(false);
    }
  }

  async function cancelDriverRequest(orderId: number) {
    if (!selectedStoreId) return;
    await fetch(`/api/order/stores/${selectedStoreId}/orders/${orderId}/driver-request`, { method: "DELETE", headers });
    await fetchOrders();
  }

  async function triggerReadyForReturn(orderId: number) {
    if (!window.confirm("This will dispatch a driver to collect the item from your shop and return it to the customer. Continue?")) return;
    try {
      const r = await fetch(`/api/order/orders/${orderId}/ready-for-return`, { method: "POST", headers });
      if (!r.ok) {
        const d = await r.json() as { error?: string };
        toast({ title: d.error ?? "Failed to dispatch return driver", variant: "destructive" });
        return;
      }
      toast({ title: "Return driver dispatched!", description: `A driver has been notified for order #${orderId}.` });
      await fetchOrders();
    } catch {
      toast({ title: "Network error. Please try again.", variant: "destructive" });
    }
  }

  if (!user) return <Redirect to="/login" />;

  const byAge = (a: Order, b: Order) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  const incoming = orders.filter(o => o.status === "pending").sort(byAge);
  const active = orders.filter(o => ["confirmed", "preparing", "ready", "out_for_delivery", "collected", "at_shop", "ready_for_return", "returning"].includes(o.status)).sort(byAge);
  const done = orders.filter(o => ["delivered", "cancelled"].includes(o.status));

  const tabRuns=externalRuns.filter(r=>tab==="runs"||tab==="incoming"&&r.status==="dispatching"||tab==="active"&&["assigned","in_progress"].includes(r.status)||tab==="done"&&["completed","cancelled"].includes(r.status));
  const tabOrders = tab === "runs" ? [] : (tab === "incoming" ? incoming : tab === "active" ? active : done);
  const deliveryMins = selectedStore ? (selectedStore.currentDeliveryMins ?? selectedStore.deliveryTimeMins) : 45;
  const collectionMins = selectedStore ? (selectedStore.currentCollectionMins ?? selectedStore.collectionTimeMins) : 20;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {selectedStore?.salesDemoKitchen && <div className="bg-amber-50 border-b border-amber-300 p-4 flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-48"><strong>Demo kitchen — practice orders only</strong><p className="text-sm">No payment, food or real driver required. Shared with the sales team.</p></div>
        <Button disabled={demoBusy} onClick={() => void sendTestOrder("collection")}>{demoBusy ? "Sending…" : "Send test collection"}</Button>
        <Button disabled={demoBusy} variant="outline" onClick={() => void sendTestOrder("store_delivery")}>Send test delivery</Button>
      </div>}
      {/* Modals */}
      {blockDriverModal && (
        <DriverBlockModal
          driverName={blockDriverModal.driverName}
          onConfirm={() => void confirmBlockDriver()}
          onCancel={() => setBlockDriverModal(null)}
          loading={blockingDriver}
        />
      )}
      {requestDriverModal && (
        <AvailableDriversModal
          drivers={availableDrivers}
          loading={loadingDrivers}
          requestingDriver={requestingDriver}
          onSelect={(driverId) => void confirmRequestDriver(driverId)}
          onCancel={() => setRequestDriverModal(null)}
        />
      )}
      {/* Driver Map overlay */}
      {showDriverMap && selectedStoreId && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl mt-4 mb-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Map size={18} className="text-primary" />
                <h2 className="font-bold text-foreground">Available Drivers</h2>
              </div>
              <button
                onClick={() => setShowDriverMap(false)}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              <AvailableDriversMap mode="store" storeId={selectedStoreId} height={500} />
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm px-4 py-3 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-3">
          {/* Store picker */}
          <div className="flex items-center gap-2 min-w-0">
            <ShoppingBag size={18} className="text-primary shrink-0" />
            {stores.length > 1 ? (
              <select
                value={selectedStoreId ?? ""}
                onChange={e => setSelectedStoreId(Number(e.target.value))}
                className="font-bold text-foreground bg-transparent border-0 outline-none text-sm"
              >
                {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            ) : (
              <span className="font-bold text-foreground text-sm truncate">{selectedStore?.name ?? "Order Pad"}</span>
            )}
          </div>

          <div className="flex items-center gap-2 ml-auto flex-wrap">
            {/* Delivery time control */}
            {selectedStore?.approved && (
              <div className="flex items-center gap-1 bg-slate-50 rounded-xl px-3 py-1.5 border border-slate-200">
                <Truck size={13} className="text-slate-400 shrink-0" />
                <button onClick={() => void changeDeliveryTime(-5)} className="w-6 h-6 rounded-full hover:bg-slate-200 flex items-center justify-center text-sm font-bold text-slate-600">−</button>
                {etaEdit?.type === "delivery" ? (
                  <form onSubmit={(e) => { e.preventDefault(); void applyCustomEta("delivery", etaEdit.value); }} className="flex items-center gap-1">
                    <input
                      autoFocus
                      type="number" min={1} value={etaEdit.value}
                      onChange={(e) => setEtaEdit({ type: "delivery", value: e.target.value })}
                      onBlur={() => void applyCustomEta("delivery", etaEdit.value)}
                      className="w-14 text-sm font-bold text-slate-800 text-center border border-primary rounded px-1 py-0.5 outline-none"
                    />
                    <span className="text-xs text-slate-400">min</span>
                  </form>
                ) : (
                  <button onClick={() => setEtaEdit({ type: "delivery", value: String(deliveryMins) })} className="text-sm font-bold text-slate-800 min-w-[40px] text-center hover:text-primary" title="Click to set custom time">
                    {fmtMins(deliveryMins)}
                  </button>
                )}
                <button onClick={() => void changeDeliveryTime(5)} className="w-6 h-6 rounded-full hover:bg-slate-200 flex items-center justify-center text-sm font-bold text-slate-600">+</button>
              </div>
            )}
            {selectedStore?.approved && (
              <div className="flex items-center gap-1 bg-slate-50 rounded-xl px-3 py-1.5 border border-slate-200">
                <Package size={13} className="text-slate-400 shrink-0" />
                <button onClick={() => void changeCollectionTime(-5)} className="w-6 h-6 rounded-full hover:bg-slate-200 flex items-center justify-center text-sm font-bold text-slate-600">−</button>
                {etaEdit?.type === "collection" ? (
                  <form onSubmit={(e) => { e.preventDefault(); void applyCustomEta("collection", etaEdit.value); }} className="flex items-center gap-1">
                    <input
                      autoFocus
                      type="number" min={1} value={etaEdit.value}
                      onChange={(e) => setEtaEdit({ type: "collection", value: e.target.value })}
                      onBlur={() => void applyCustomEta("collection", etaEdit.value)}
                      className="w-14 text-sm font-bold text-slate-800 text-center border border-primary rounded px-1 py-0.5 outline-none"
                    />
                    <span className="text-xs text-slate-400">min</span>
                  </form>
                ) : (
                  <button onClick={() => setEtaEdit({ type: "collection", value: String(collectionMins) })} className="text-sm font-bold text-slate-800 min-w-[40px] text-center hover:text-primary" title="Click to set custom time">
                    {fmtMins(collectionMins)}
                  </button>
                )}
                <button onClick={() => void changeCollectionTime(5)} className="w-6 h-6 rounded-full hover:bg-slate-200 flex items-center justify-center text-sm font-bold text-slate-600">+</button>
              </div>
            )}

            {/* Pause toggle */}
            {selectedStore?.approved && (
              <div className="relative">
                <button
                  onClick={() => selectedStore.acceptingOrders ? setShowPausePicker(p => !p) : void pauseFor("resume")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-semibold border transition-colors ${selectedStore.acceptingOrders ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100" : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"}`}
                >
                  {selectedStore.acceptingOrders ? <><Play size={13} /> Open</> : <><Pause size={13} /> Paused — tap to resume</>}
                </button>
                {showPausePicker && selectedStore.acceptingOrders && (
                  <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-xl shadow-lg p-2 w-48 flex flex-col gap-1">
                    <p className="text-xs font-semibold text-slate-500 px-2 py-1">Pause for how long?</p>
                    {([
                      { label: "15 minutes", mins: 15 },
                      { label: "1 hour", mins: 60 },
                      { label: "Rest of the day", mins: -1 },
                    ] as const).map(({ label, mins }) => (
                      <button key={label} onClick={() => void pauseFor(mins)}
                        className="text-left px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg transition-colors">
                        {label}
                      </button>
                    ))}
                    <button onClick={() => setShowPausePicker(false)} className="text-left px-3 py-1 text-xs text-slate-400 hover:text-slate-600">Cancel</button>
                  </div>
                )}
              </div>
            )}

            {/* Alert toggle */}
            <button
              onClick={() => setAlertsOn(!alertsOn)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-semibold border transition-colors ${alertsOn ? "bg-primary/10 text-primary border-primary/20" : "bg-slate-100 text-slate-500 border-slate-200"}`}
            >
              {alertsOn ? <Bell size={13} /> : <BellOff size={13} />}
              {alertsOn ? "Alerts on" : "Muted"}
            </button>

            {/* Driver Map */}
            {selectedStoreId && (
              <button
                onClick={() => setShowDriverMap(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-semibold border transition-colors bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
              >
                <Map size={13} /> Drivers
              </button>
            )}

            {/* Manual refresh */}
            <button onClick={() => void fetchOrders()} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 border border-slate-200">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </div>

      {/* Not approved notice */}
      {selectedStore && !selectedStore.approved && (
        <div className="max-w-5xl mx-auto w-full px-4 pt-6">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-2xl p-6 text-center">
            <Clock size={36} className="mx-auto text-amber-400 mb-3" />
            <h3 className="font-bold text-amber-800 dark:text-amber-300 text-lg">Awaiting Approval</h3>
            <p className="text-amber-600 text-sm mt-1">Your store is pending admin approval. You'll be able to take orders once approved.</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      {selectedStore?.approved && (
        <div className="bg-white border-b border-slate-200 px-4">
          <div className="max-w-5xl mx-auto flex gap-0 overflow-x-auto">
            {[
              { key: "incoming", label: "Incoming", count: incoming.length+externalRuns.filter(r=>r.status==="dispatching").length, color: incoming.length > 0 ? "bg-amber-500" : "" },
              { key: "active",   label: "Active",   count: active.length+externalRuns.filter(r=>["assigned","in_progress"].includes(r.status)).length,   color: active.length > 0 ? "bg-primary" : "" },
              { key: "done",     label: "Done",     count: done.length+externalRuns.filter(r=>["completed","cancelled"].includes(r.status)).length,     color: "" },
              { key: "runs",     label: "Deliveries", count: externalRuns.length, color: externalRuns.length > 0 ? "bg-indigo-500" : "" },
            ].map(({ key, label, count, color }) => (
              <button
                key={key}
                onClick={() => setTab(key as typeof tab)}
                className={`flex items-center gap-2 px-3 sm:px-5 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${tab === key ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              >
                {label}
                {count > 0 && (
                  <span className={`text-xs text-white rounded-full w-5 h-5 flex items-center justify-center font-bold ${color || "bg-slate-400"}`}>{count}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Date navigator — browse past trading days (matches mobile pad) */}
      {selectedStore?.approved && (
        <div className="bg-white border-b border-slate-100">
          <div className="max-w-5xl mx-auto flex items-center justify-center gap-2 px-4 py-2">
            <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setViewDate(addDaysYMD(viewDate, -1))}>
              <ChevronLeft size={16} />
            </Button>
            <input
              type="date"
              value={viewDate}
              max={todayYMD()}
              onChange={(e) => { if (e.target.value) setViewDate(e.target.value); }}
              className="border rounded-lg px-2 py-1 text-sm"
            />
            <span className="text-xs text-slate-500 w-14">{isToday ? "Today" : new Date(viewDate + "T12:00:00").toLocaleDateString("en-GB", { weekday: "short" })}</span>
            <Button size="sm" variant="ghost" className="h-8 px-2" disabled={isToday} onClick={() => setViewDate(addDaysYMD(viewDate, 1))}>
              <ChevronRight size={16} />
            </Button>
            {!isToday && <Button size="sm" variant="outline" className="h-8 text-xs rounded-lg" onClick={() => setViewDate(todayYMD())}>Today</Button>}
          </div>
        </div>
      )}

      {/* Day stats strip — orders / revenue kept / done (matches mobile pad) */}
      {selectedStore?.approved && (
        <div className="bg-white border-b border-slate-100">
          <div className="max-w-5xl mx-auto grid grid-cols-3 divide-x divide-slate-100">
            {(() => {
              const live = orders.filter((o) => o.status !== "cancelled");
              const revenue = live.reduce((sum, o) => sum + orderCharges(o).youKeep, 0);
              const cells = [
                { label: "Orders", value: String(orders.length) },
                { label: "Revenue", value: pence(revenue) },
                { label: "Done", value: String(done.length) },
              ];
              return cells.map((c) => (
                <div key={c.label} className="px-4 py-2.5 text-center">
                  <div className="text-lg font-black text-foreground tabular-nums">{c.value}</div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{c.label}</div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* Recurring orders modal */}
      {showRecurringModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <Repeat size={16} className="text-blue-600" />
                <h2 className="font-bold text-foreground">Recurring Orders</h2>
                <span className="bg-blue-100 text-blue-700 text-xs font-bold rounded-full px-2 py-0.5">{recurringSchedules.length}</span>
              </div>
              <button onClick={() => setShowRecurringModal(false)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                <X size={16} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {recurringSchedules.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6">No active recurring orders</p>
              ) : recurringSchedules.map((s) => {
                let items: Array<{ name: string; quantity: number }> = [];
                try { items = JSON.parse(s.itemsJson) as typeof items; } catch { /* ignore */ }
                const nextDate = new Date(s.nextRunAt);
                const freqLabel = s.frequency === "daily" ? "Daily" : "Weekly";
                return (
                  <div key={s.id} className="border border-slate-100 rounded-xl p-4 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-semibold text-slate-800 text-sm">{s.customerName ?? "Customer"}</div>
                        {s.customerPhone && (
                          <a href={`tel:${s.customerPhone}`} className="text-xs text-primary hover:underline">{s.customerPhone}</a>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs font-bold text-blue-600">{freqLabel} at {s.preferredTime}</div>
                        <div className="text-xs text-slate-400">{s.runsRemaining}/{s.totalRuns} remaining</div>
                      </div>
                    </div>
                    <div className="text-xs text-slate-600 space-y-0.5">
                      {(items as Array<{ name: string; quantity: number; isFreeItem?: boolean }>).filter((i) => !i.isFreeItem).map((item, i) => (
                        <div key={i}>{item.quantity}× {item.name}</div>
                      ))}
                    </div>
                    {s.deliveryAddress && (
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <MapPin size={11} /> {s.deliveryAddress}
                      </div>
                    )}
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-400">Next order</span>
                      <span className="font-semibold text-emerald-700">
                        {nextDate.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} at {s.preferredTime}
                      </span>
                    </div>
                    {s.discountPercent > 0 && (
                      <div className="text-xs text-blue-600 font-medium">🏷 {s.discountPercent}% recurring discount applied</div>
                    )}
                    <div className="text-xs font-semibold text-slate-700">{pence(s.totalPerRunPence)} per order</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Recurring orders bar — collapsed, tap to expand */}
      {selectedStore?.approved && recurringSchedules.length > 0 && (
        <div className="max-w-5xl mx-auto w-full px-4 pt-3">
          <button
            onClick={() => setShowRecurringModal(true)}
            className="w-full flex items-center justify-between gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-xl px-4 py-2.5 hover:bg-blue-100 transition-colors group"
          >
            <div className="flex items-center gap-2 text-sm font-semibold text-blue-700">
              <Repeat size={15} className="shrink-0" />
              <span>{recurringSchedules.length} recurring order{recurringSchedules.length !== 1 ? "s" : ""} active</span>
            </div>
            <ChevronRight size={15} className="text-blue-400 group-hover:text-blue-600 transition-colors" />
          </button>
        </div>
      )}

      {selectedStoreId&&!selectedStore?.salesDemoKitchen&&<div className="max-w-5xl mx-auto w-full px-4 pt-4"><button onClick={()=>setNewRun(true)} className="min-h-12 rounded-xl bg-[#1e3a5f] text-white px-5 font-semibold">Request multi-drop driver</button>{runsError&&<div role="alert" className="p-3 mt-3 rounded-xl bg-amber-50 text-amber-900 text-sm">{runsError}<button className="underline ml-3" onClick={()=>void fetchExternalRuns()}>Retry</button></div>}</div>}
      {newRun&&selectedStoreId&&<NewRunRequest storeId={selectedStoreId} storeName={selectedStore?.name??"Your shop"} headers={headers} onClose={()=>setNewRun(false)} onBooked={()=>{setNewRun(false);setTab("incoming");void fetchExternalRuns();}}/>}
      {/* Orders grid */}
      {selectedStore?.approved && (
        <div className="max-w-5xl mx-auto w-full px-4 py-5 flex-1">
          {loading && tabOrders.length === 0 ? (
            <div className="flex items-center justify-center h-48">
              <RefreshCw size={24} className="animate-spin text-slate-300" />
            </div>
          ) : tab === "runs" && externalRuns.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Truck size={48} className="text-slate-200 mb-4" />
              <h3 className="font-semibold text-slate-600">No delivery runs</h3>
              <p className="text-sm text-slate-400 mt-1">You haven't created any external delivery runs yet</p>
            </div>
          ) : tab === "runs" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <RunListSection key={`runs-${selectedStoreId}-${tab}`} runs={tabRuns} headers={headers} onChanged={fetchExternalRuns} />
            </div>
          ) : tabOrders.length === 0&&tabRuns.length===0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              {tab === "incoming" ? (
                <>
                  <Bell size={48} className="text-slate-200 mb-4" />
                  <h3 className="font-semibold text-slate-600">No new orders</h3>
                  <p className="text-sm text-slate-400 mt-1">Waiting for orders… <span className="text-emerald-600 font-medium">{selectedStore.acceptingOrders ? "You're open" : "Orders paused"}</span></p>
                </>
              ) : tab === "active" ? (
                <>
                  <CheckCircle size={48} className="text-slate-200 mb-4" />
                  <h3 className="font-semibold text-slate-600">No active orders</h3>
                </>
              ) : (
                <>
                  <ShoppingBag size={48} className="text-slate-200 mb-4" />
                  <h3 className="font-semibold text-slate-600">No completed orders yet</h3>
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {tabOrders.map(order => (
                <OrderCard
                  key={order.id}
                  order={order}
                  storeId={selectedStoreId ?? 0}
                  onStatusChange={updateStatus}
                  onAccept={acceptOrder}
                  deliveryMins={deliveryMins}
                  collectionMins={collectionMins}
                  onReject={rejectOrder}
                  onBanFromCash={banFromCash}
                  onBlockDriver={(driverId, driverName) => setBlockDriverModal({ driverId, driverName })}
                  onRequestDriver={openRequestDriverModal}
                  onCancelDriverRequest={cancelDriverRequest}
                  onReadyForReturn={triggerReadyForReturn}
                  onProblem={o => { if (o.isDemo) { if (window.confirm("Cancel this practice order? No payment or refund is involved.")) void updateStatus(o.id, "cancelled"); } else setProblemOrder(o); }}
                  onExtendEta={extendEta}
                  onReassignDriver={reassignDriver}
                  onMessageDriver={setDriverChatOrder}
                />
              ))}
              <RunListSection key={`runs-${selectedStoreId}-${tab}`} runs={tabRuns} headers={headers} onChanged={fetchExternalRuns} />
            </div>
          )}
        </div>
      )}

      {/* Order problem modal (refund item / suggest replacement / message / cancel) */}
      {problemOrder && (
        <OrderProblemModal
          order={problemOrder}
          headers={headers}
          onDone={() => { void fetchOrders(); }}
          onCancelWhole={rejectOrder}
          onClose={() => setProblemOrder(null)}
        />
      )}

      {/* Driver chat modal */}
      {driverChatOrder && (
        <DriverChatModal
          order={driverChatOrder}
          headers={headers}
          onClose={() => setDriverChatOrder(null)}
        />
      )}

      {/* Polling indicator */}
      <div className="fixed bottom-3 right-4 text-xs text-slate-300 flex items-center gap-1">
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        Live
      </div>
    </div>
  );
}

function RunListSection({runs,headers,onChanged}:{runs:PadRun[];headers:Record<string,string>;onChanged:()=>void|Promise<void>}) {
  return <>{runs.map(run=><RunOrderCard key={`run-${run.id}`} run={run} headers={headers} onChanged={onChanged}/>)}</>;
}
